import {
	estimateDisplayAtom,
	estimateServiceBreakdownAtom,
	freeServicesAtom,
	hasPaidServicePricing,
	rawAiCostBreakdownAtom,
	rawSeasonalPricingAtom,
	serviceAvailabilityAtom,
	startingCostAtom,
} from '@client/jotai/storefront/pricing';
import { usePersistedEstimate } from '@hooks/use-persisted-estimate';
import type { PersistedEstimate } from '@hooks/use-persisted-estimate';
import { SidebarModal, SidebarModalPane } from '@tkww/orion-web-ds-dialog';
import { compose } from '@xo-union/react-css-modules';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { displayCostValue } from '../../../hooks/use-pricing-estimation';
import type { PricingEstimationService } from '../../../types';
import { useTrackSaveNewEstimate } from '../../hooks/useTrackSaveNewEstimate';
import {
	type EstimateServiceBreakdown,
	type EstimateServiceBreakdownItem,
	SERVICES,
	buildEstimateDisplay,
	displayEstimateServicePrice,
} from '../../utils/estimateDisplay';
import {
	resolveEstimateServiceSourceType,
	resolveEstimateSourceType,
} from '../../utils/estimateSource';
import { getGuestCountError } from '../../utils/guestCount';
import { setPersistedFormInput } from '../../utils/persistedFormInput';
import {
	type WeddingDateData,
	getWeddingDateError,
} from '../../utils/weddingDate';
import { useCalculateEstimate } from '../EstimateFormModal/hooks/useCalculateEstimate';
import { EditEstimateContent } from './EditEstimateContent';
import { ExitConfirmation } from './ExitConfirmation';
import { useEditEstimateFormState } from './hooks/useEditEstimateFormState';
import Styles from './styles.scss';

interface EditEstimateFormModalProps {
	isModalOpen: boolean;
	closeCallback: () => void;
}

export const EditEstimateFormModal = ({
	isModalOpen,
	closeCallback,
}: EditEstimateFormModalProps) => {
	const estimate = useAtomValue(estimateDisplayAtom);
	const serviceBreakdown = useAtomValue(estimateServiceBreakdownAtom);
	const setEstimateDisplay = useSetAtom(estimateDisplayAtom);
	const setEstimateServiceBreakdown = useSetAtom(estimateServiceBreakdownAtom);

	const storefrontId = useSelector(
		(state: Redux.State) =>
			state.rfq.storefrontId || state.vendor.vendorRaw?.id || '',
	);
	const { estimate: persistedEstimate, setEstimate: persistEstimate } =
		usePersistedEstimate(storefrontId);

	const {
		selectedServices,
		weddingDate,
		setWeddingDate,
		isFormDirty,
		toggleService,
		guestCount,
		setGuestCount,
	} = useEditEstimateFormState({
		isModalOpen,
		persistedEstimate,
		serviceBreakdown,
	});

	const [isExitModalOpen, setIsExitModalOpen] = useState(false);
	const trackSaveNewEstimate = useTrackSaveNewEstimate();
	const isSavingRef = useRef(false);
	const closeButtonRef = useRef<HTMLButtonElement>(null);
	const exitDialogRef = useRef<HTMLDivElement>(null);
	const exitCancelButtonRef = useRef<HTMLButtonElement>(null);
	const wasExitModalOpenRef = useRef(false);

	useEffect(() => {
		if (!isModalOpen) {
			setIsExitModalOpen(false);
			isSavingRef.current = false;
		}
	}, [isModalOpen]);

	// Restore focus to the close button when the exit confirmation is dismissed
	// while the sidebar is still open (Go back / backdrop / Escape paths).
	useEffect(() => {
		if (wasExitModalOpenRef.current && !isExitModalOpen && isModalOpen)
			closeButtonRef.current?.focus();
		wasExitModalOpenRef.current = isExitModalOpen;
	}, [isExitModalOpen, isModalOpen]);

	useEffect(() => {
		if (isExitModalOpen) exitCancelButtonRef.current?.focus();
	}, [isExitModalOpen]);

	const handleCloseAttempt = () => {
		isFormDirty ? setIsExitModalOpen(true) : closeCallback();
	};

	const handleSidebarClose = () => {
		if (isSavingRef.current) {
			isSavingRef.current = false;
			return;
		}
		if (isExitModalOpen) {
			setIsExitModalOpen(false);
			return;
		}
		handleCloseAttempt();
	};

	const isGuestCountValid = !getGuestCountError(guestCount);
	const validatedGuestCount = isGuestCountValid ? guestCount : '';

	const weddingDateData: WeddingDateData = weddingDate
		? {
				dateOption: 'specificDate',
				specificDate: weddingDate,
				month: '',
				year: '',
			}
		: { dateOption: '', specificDate: '', month: '', year: '' };

	const liveEstimate = useCalculateEstimate(
		validatedGuestCount,
		weddingDateData,
		selectedServices,
	);

	const allServicesEstimate = useCalculateEstimate(
		validatedGuestCount,
		weddingDateData,
		SERVICES.map(({ key }) => key),
	);

	// Read live, not from the persisted estimate, so a service the venue
	// re-enables after the estimate was created shows up as available right away.
	const serviceAvailability = useAtomValue(serviceAvailabilityAtom);
	const startingCost = useAtomValue(startingCostAtom);
	const rawSeasonalPricing = useAtomValue(rawSeasonalPricingAtom);
	const rawAiCostBreakdown = useAtomValue(rawAiCostBreakdownAtom);
	const freeServices = useAtomValue(freeServicesAtom);
	const hasMinimumSpend = Boolean(startingCost);
	const hasPaidSelectedService = hasPaidServicePricing({
		services: selectedServices,
		seasonalPricing: rawSeasonalPricing,
		aiCostBreakdown: rawAiCostBreakdown,
		freeServices,
		serviceAvailability,
	});
	const canSaveEstimate = hasMinimumSpend || hasPaidSelectedService;
	const unavailableServiceKeys = useMemo(
		() =>
			new Set(
				SERVICES.filter(({ key }) => !serviceAvailability[key]).map(
					({ key }) => key,
				),
			),
		[serviceAvailability],
	);

	const servicePrices = useMemo((): Partial<
		Record<PricingEstimationService, EstimateServiceBreakdownItem | null>
	> => {
		return Object.fromEntries(
			SERVICES.map(({ key }) => {
				if (unavailableServiceKeys.has(key))
					return [key, { unavailable: true }];
				const p = allServicesEstimate?.[key];
				if (p) {
					return [
						key,
						{
							price: displayEstimateServicePrice(p).replace(' per guest', ''),
							sourceType: resolveEstimateServiceSourceType(p.dataSource),
							isFree: p.isFree,
						},
					];
				}
				const persisted = serviceBreakdown?.[key];
				if (!persisted) return [key, null];
				if (typeof persisted !== 'string' && 'unavailable' in persisted)
					return [key, { unavailable: true }];
				const persistedPrice =
					typeof persisted === 'string' ? persisted : persisted.price;
				const persistedSourceType =
					typeof persisted === 'string' ? 'VENDOR' : persisted.sourceType;
				const persistedIsFree =
					typeof persisted === 'string' ? false : persisted.isFree;
				return [
					key,
					persistedPrice
						? {
								price: persistedPrice.replace(' per guest', ''),
								sourceType: persistedSourceType,
								isFree: persistedIsFree,
							}
						: null,
				];
			}),
		);
	}, [allServicesEstimate, serviceBreakdown, unavailableServiceKeys]);

	// Unavailable services stay checked (but disabled) forever, so
	// `selectedServices` alone can't detect "no available service selected".
	const hasNoAvailableServiceSelected = selectedServices.every(
		(key) => servicePrices[key] && 'unavailable' in servicePrices[key],
	);

	const hasFormErrors =
		!isGuestCountValid ||
		!!getWeddingDateError(weddingDateData) ||
		hasNoAvailableServiceSelected;

	const previewPrice = useMemo(() => {
		if (hasFormErrors) return '$0';
		if (!liveEstimate || !persistedEstimate) return estimate?.price ?? null;
		return displayCostValue({
			minCost: liveEstimate.totalCost.minCost,
			maxCost: liveEstimate.totalCost.maxCost,
		});
	}, [hasFormErrors, liveEstimate, persistedEstimate, estimate?.price]);

	const estimateSourceType = liveEstimate
		? resolveEstimateSourceType(liveEstimate, selectedServices)
		: estimate?.estimateSourceType;

	const handleSave = () => {
		if (
			!canSaveEstimate ||
			!liveEstimate ||
			!persistedEstimate ||
			!isGuestCountValid
		)
			return;

		const newWeddingDate: PersistedEstimate['weddingDate'] = {
			type: 'exact',
			date: weddingDate,
		};
		const estimateDisplay = buildEstimateDisplay({
			selections: Object.fromEntries(selectedServices.map((s) => [s, true])),
			weddingDate: newWeddingDate,
			guestCount: Number(guestCount),
			calculatedTotal: {
				min: liveEstimate.totalCost.minCost,
				max: liveEstimate.totalCost.maxCost,
				isBelowMinimumSpend: liveEstimate.totalCost.isBelowMinimumSpend,
				originalMinCost: liveEstimate.totalCost.originalMinCost,
			},
			estimateSourceType: resolveEstimateSourceType(
				liveEstimate,
				selectedServices,
			),
		});
		setEstimateDisplay(estimateDisplay);

		const newServiceBreakdown: EstimateServiceBreakdown = {};
		for (const { key } of SERVICES) {
			const p = liveEstimate[key];
			if (p) {
				newServiceBreakdown[key] = {
					price: displayEstimateServicePrice(p),
					sourceType: resolveEstimateServiceSourceType(p.dataSource),
					isFree: p.isFree,
				};
			}
		}
		// Selected services the venue currently doesn't offer have no calculated
		// price; mark them unavailable from current availability (not the old
		// breakdown snapshot) so the cost breakdown keeps rendering "Not offered"
		// even if availability changed since the estimate was created.
		for (const { key } of SERVICES) {
			if (newServiceBreakdown[key] || !selectedServices.includes(key)) continue;
			if (unavailableServiceKeys.has(key))
				newServiceBreakdown[key] = { unavailable: true };
		}
		setEstimateServiceBreakdown(newServiceBreakdown);

		if (storefrontId) {
			persistEstimate({
				...persistedEstimate,
				weddingDate: newWeddingDate,
				guestCount: Number(guestCount),
				selections: Object.fromEntries(selectedServices.map((s) => [s, true])),
				estimateSourceType: resolveEstimateSourceType(
					liveEstimate,
					selectedServices,
				),
				serviceBreakdown: newServiceBreakdown,
				unavailableServices: Array.from(unavailableServiceKeys),
				calculatedTotal: {
					min: liveEstimate.totalCost.minCost,
					max: liveEstimate.totalCost.maxCost,
					isBelowMinimumSpend: liveEstimate.totalCost.isBelowMinimumSpend,
					originalMinCost: liveEstimate.totalCost.originalMinCost,
				},
				lastUpdated: new Date().toISOString(),
			});
		}

		setPersistedFormInput({
			guestCount,
			weddingDate: weddingDateData,
			selectedServices,
		});

		trackSaveNewEstimate();

		isSavingRef.current = true;
		closeCallback();
	};

	return (
		<SidebarModal
			isOpen={isModalOpen}
			onClose={handleSidebarClose}
			anchor="right"
		>
			<SidebarModalPane
				header={false}
				classes={compose({
					content: Styles.paneContent,
					footer: Styles.ctaFooter,
					'button-container': Styles.ctaButtonContainer,
				})}
				primaryAction={
					isExitModalOpen
						? undefined
						: {
								children: 'Save new estimate',
								disabled:
									!isFormDirty ||
									!liveEstimate ||
									!persistedEstimate ||
									!canSaveEstimate ||
									hasFormErrors,
								onClick: handleSave,
							}
				}
			>
				{isExitModalOpen ? (
					<ExitConfirmation
						cancelButtonRef={exitCancelButtonRef}
						dialogRef={exitDialogRef}
						onGoBack={() => setIsExitModalOpen(false)}
						onLeave={closeCallback}
					/>
				) : (
					<EditEstimateContent
						closeButtonRef={closeButtonRef}
						onClose={handleCloseAttempt}
						previewPrice={previewPrice}
						selectedServices={selectedServices}
						onToggleService={toggleService}
						servicePrices={servicePrices}
						showMinimumSpendInfo={!hasMinimumSpend && !hasPaidSelectedService}
						guestCount={guestCount}
						onGuestCountChange={setGuestCount}
						weddingDate={weddingDate}
						onWeddingDateChange={setWeddingDate}
						isModalOpen={isModalOpen}
						estimateSourceType={estimateSourceType}
					/>
				)}
			</SidebarModalPane>
		</SidebarModal>
	);
};
